/**
 * Marketer-facing explanations of marketing router concepts.
 *
 * Every hover explanation in the rules list, the rule form and the results
 * page reads from here, so one concept is never described two ways. The
 * wording lives in admin for now; it moves into the contract once settled,
 * as the overlap copy did.
 */
export type MarketingRouterTerm = 'rule' | 'treatment' | 'metrics' | 'primaryMetric' | 'confidence' | 'weight';

export type MarketingRouterTermEntry = {
  /** How the term is written where it appears as a heading or label. */
  label: string;
  /** The hover explanation. One or two sentences. */
  description: string;
};

export const MARKETING_ROUTER_GLOSSARY: Record<MarketingRouterTerm, MarketingRouterTermEntry> = {
  rule: {
    label: 'Rule',
    description:
      'One experiment. A rule says who is eligible (match conditions and dates), how eligible visitors are split into variants, and which metrics decide the outcome.',
  },
  treatment: {
    label: 'Treatment',
    description:
      'What a variant does to the visitor’s experience, such as showing a promo popup or enabling MLOS. A variant can carry several; the control usually carries none.',
  },
  metrics: {
    label: 'Metrics',
    description:
      'The measurements used to compare each variant with the control. The first metric is the primary metric and decides the outcome.',
  },
  primaryMetric: {
    label: 'Primary metric',
    description: 'The first metric in the list. The Decision on the Results page is based on this metric alone.',
  },
  confidence: {
    label: 'Confidence',
    description:
      'How reliable the measured difference is. Higher confidence means the effect is less likely to be noise; 95% or more is treated as significant.',
  },
  weight: {
    label: 'Weight (%)',
    description: 'The percentage of eligible visitors assigned to this variant. Variant weights should total 100%.',
  },
};

export function getMarketingRouterTerm(term: MarketingRouterTerm): MarketingRouterTermEntry {
  return MARKETING_ROUTER_GLOSSARY[term];
}
